import React, { useContext } from "react";
import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AppVarsContext } from "../../context/AppVarsContext";

const GlassPageShell = ({
  eyebrow,
  title,
  description,
  badge,
  heroAside,
  children,
  maxWidth = 1480,
}) => {
  const { appAppearance } = useContext(AppVarsContext);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        background: appAppearance.colors.pageBackground,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.55,
          backgroundImage: `linear-gradient(${appAppearance.colors.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${appAppearance.colors.gridLine} 1px, transparent 1px)`,
          backgroundSize: "44px 44px",
          maskImage: "linear-gradient(180deg, rgba(0,0,0,0.72), transparent 88%)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "relative",
          maxWidth,
          mx: "auto",
          px: { xs: 1.25, sm: 2.5, lg: 4 },
          py: { xs: 1.5, sm: 2.5, md: 3.5 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: { xs: 4, md: 6 },
            border: `1px solid ${appAppearance.colors.borderStrong}`,
            background: `linear-gradient(180deg, ${alpha("#ffffff", 0.82)} 0%, ${alpha("#ffffff", 0.74)} 100%)`,
            boxShadow: appAppearance.shadows.shell,
            backdropFilter: "blur(24px)",
          }}
        >
          <Box
            sx={{
              position: "relative",
              px: { xs: 2, sm: 3, md: 4 },
              pt: { xs: 2.5, md: 3.5 },
              pb: { xs: 2.5, md: 3 },
              borderBottom: `1px solid ${appAppearance.colors.divider}`,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: appAppearance.colors.heroWash,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                right: { xs: -40, md: -70 },
                top: { xs: -60, md: -80 },
                width: { xs: 180, md: 280 },
                height: { xs: 180, md: 280 },
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha(appAppearance.colors.accent, 0.18)} 0%, transparent 68%)`,
                filter: "blur(10px)",
              }}
            />
            <Box
              sx={{
                position: "relative",
                display: "grid",
                gridTemplateColumns: heroAside
                  ? { xs: "1fr", xl: "minmax(0, 1.25fr) minmax(280px, 0.75fr)" }
                  : "1fr",
                gap: 2.5,
                alignItems: "stretch",
              }}
            >
              <Stack spacing={1.5} justifyContent="center">
                {eyebrow ? (
                  <Typography
                    variant="overline"
                    sx={{
                      color: appAppearance.colors.heroText,
                      letterSpacing: "0.16em",
                      fontWeight: 700,
                      opacity: 0.86,
                    }}
                  >
                    {eyebrow}
                  </Typography>
                ) : null}
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "flex-start", md: "center" }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      color: appAppearance.colors.heroText,
                      fontSize: { xs: "2rem", md: "3rem" },
                      lineHeight: 1.02,
                      letterSpacing: "-0.04em",
                      fontWeight: 800,
                    }}
                  >
                    {title}
                  </Typography>
                  {badge ? (
                    <Chip
                      label={badge}
                      sx={{
                        height: 34,
                        borderRadius: "999px",
                        bgcolor: alpha("#ffffff", 0.26),
                        color: appAppearance.colors.heroText,
                        border: `1px solid ${alpha("#ffffff", 0.34)}`,
                        backdropFilter: "blur(10px)",
                        "& .MuiChip-label": {
                          px: 1.5,
                          fontWeight: 700,
                        },
                      }}
                    />
                  ) : null}
                </Stack>
                {description ? (
                  <Typography
                    variant="body1"
                    sx={{
                      maxWidth: 760,
                      color: alpha(appAppearance.colors.heroText, 0.84),
                      fontSize: { xs: "0.98rem", md: "1.05rem" },
                      lineHeight: 1.65,
                    }}
                  >
                    {description}
                  </Typography>
                ) : null}
              </Stack>
              {heroAside ? (
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {heroAside}
                </Box>
              ) : null}
            </Box>
          </Box>
          <Box
            sx={{
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 2, sm: 2.5, md: 3 },
              display: "grid",
              gap: { xs: 2, md: 2.5 },
            }}
          >
            {children}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default GlassPageShell;
